import { Injectable, OnDestroy, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { MatDialog } from '@angular/material/dialog';
import { ViewportService } from '@ui';
import { MobileBackButtonModels } from '../models/mobile-back-button.model';

@Injectable({
  providedIn: 'root',
})
export class MobileBackButtonService implements OnDestroy {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly matDialog: MatDialog = inject(MatDialog);

  private readonly overlayHandlers: MobileBackButtonModels.OverlayHandler[] =
    [];
  private backButtonListener: PluginListenerHandle | null = null;
  private isInitialized = false;

  public init(): void {
    if (this.isInitialized || !this.viewportService.isNativeAndroid()) {
      return;
    }

    this.isInitialized = true;

    App.addListener('backButton', ({ canGoBack }) => this.handleBack(canGoBack))
      .then((listener) => (this.backButtonListener = listener))
      .catch((error) =>
        console.warn(
          '[MobileBackButtonService] Failed to register back button listener:',
          error,
        ),
      );
  }

  public registerOverlay(
    handler: MobileBackButtonModels.OverlayHandler,
  ): () => void {
    this.overlayHandlers.push(handler);

    return () => this.unregisterOverlay(handler);
  }

  public unregisterOverlay(
    handler: MobileBackButtonModels.OverlayHandler,
  ): void {
    const index = this.overlayHandlers.lastIndexOf(handler);

    if (index !== -1) {
      this.overlayHandlers.splice(index, 1);
    }
  }

  private handleBack(canGoBack: boolean): void {
    if (this.closeTopDialog()) {
      return;
    }

    if (this.closeTopOverlay()) {
      return;
    }

    if (canGoBack) {
      window.history.back();
      return;
    }

    void App.exitApp();
  }

  private closeTopDialog(): boolean {
    const openDialogs = this.matDialog.openDialogs;

    if (openDialogs.length === 0) {
      return false;
    }

    openDialogs[openDialogs.length - 1].close();

    return true;
  }

  private closeTopOverlay(): boolean {
    for (let index = this.overlayHandlers.length - 1; index >= 0; index--) {
      if (this.overlayHandlers[index]()) {
        return true;
      }
    }

    return false;
  }

  public ngOnDestroy(): void {
    void this.backButtonListener?.remove();
    this.backButtonListener = null;
    this.overlayHandlers.length = 0;
    this.isInitialized = false;
  }
}
